import { useEffect, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from 'utils/firebase-config';
import { toast } from 'react-toastify';
import { api } from 'store/services/general.services';

const SEEN_KEY = 'seenAdminMessagePks';

interface AdminMessage {
  pk: number;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

function getSeen(): Set<number> {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    return new Set<number>(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set<number>();
  }
}

function saveSeen(seen: Set<number>) {
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seen)));
  } catch {
    // sessionStorage unavailable — dedup falls back to in-memory only.
  }
}

/**
 * App-wide listener: when the backend bumps `adminNotifications/lastSentAt`,
 * toast any admin broadcasts this session hasn't seen yet. Mirrors the
 * join-request notification pattern (Firebase signal -> react-toastify).
 *
 * The first Firebase fire on subscribe is treated as a baseline (record what
 * already exists, no toast), so page loads/refreshes don't replay old messages;
 * only genuinely new broadcasts toast.
 */
export function useAdminMessageNotifications() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userPk = user?.pk;
  const seenRef = useRef<Set<number>>(getSeen());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!userPk) {
      return undefined;
    }

    const process = (messages: AdminMessage[], isBaseline: boolean) => {
      const fresh = messages.filter(m => !seenRef.current.has(m.pk));
      if (!fresh.length) {
        return;
      }
      if (!isBaseline) {
        fresh.forEach(m => toast.info(`New message from Admin: ${m.title}`));
      }
      fresh.forEach(m => seenRef.current.add(m.pk));
      saveSeen(seenRef.current);
    };

    const fetchAndProcess = (isBaseline: boolean) => {
      api
        .adminMessagesRequest()
        .then((res: any) => {
          if (Array.isArray(res)) {
            process(res, isBaseline);
          }
        })
        .catch(() => {});
    };

    const signalRef = ref(db, 'adminNotifications/lastSentAt');
    const unsubscribe = onValue(signalRef, () => {
      const isBaseline = !initializedRef.current;
      initializedRef.current = true;
      fetchAndProcess(isBaseline);
    });

    return () => unsubscribe();
  }, [userPk]);
}
